import { X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { IconButton } from '@/components/ui/icon-button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { PANEL_REGISTRY, type PanelGroup } from './panelRegistry';

type Props = {
  open: boolean;
  onClose: () => void;
  panelState: Record<string, boolean>;
  onToggle: (id: string) => void;
};

const GROUP_ORDER: PanelGroup[] = ['overview', 'context', 'params'];

const GROUP_KEYS: Record<PanelGroup, string> = {
  overview: 'analytics.dashboard.drawer.group.overview',
  context: 'analytics.dashboard.drawer.group.context',
  params: 'analytics.dashboard.drawer.group.params',
};

export function PanelDrawer({ open, onClose, panelState, onToggle }: Props) {
  const { t } = useTranslation();

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        className={cn(
          'fixed right-0 top-0 bottom-0 w-[340px] bg-card border-l border-border z-50 flex flex-col',
          'transition-transform duration-300',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label={t('analytics.dashboard.drawer.title')}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <span className="text-sm font-semibold text-foreground">
            {t('analytics.dashboard.drawer.title')}
          </span>
          <IconButton icon={X} label={t('common.close')} onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {GROUP_ORDER.map((group) => {
            const panels = PANEL_REGISTRY.filter((p) => p.group === group);
            return (
              <div key={group} className="mb-6">
                <h3 className="eyebrow mb-3">
                  {t(GROUP_KEYS[group])}
                </h3>
                <div className="flex flex-col gap-3">
                  {panels.map((panel) => (
                    <div key={panel.id} className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">{t(panel.nameKey)}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{t(panel.descKey)}</div>
                      </div>
                      <Switch
                        checked={panelState[panel.id] ?? panel.defaultOn}
                        onCheckedChange={() => onToggle(panel.id)}
                        aria-label={t(panel.nameKey)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
